if (!customElements.get('product-info')) {
  class ProductInfo extends HTMLElement {
    static swapInProgress = false;
    static scrollToTopAfterSwap = false;

      constructor() {
        super();

        this.quantityInput = undefined;
        this.quantityForm = undefined;
        this.onVariantChangeUnsubscriber = undefined;
        this.cartUpdateUnsubscriber = undefined;
        this.abortController = undefined;
        this.pendingRequestUrl = null;
        this.preProcessHtmlCallbacks = [];
        this.postProcessHtmlCallbacks = [];
        this.zoomModalAbortController = undefined;

        this.productFormObserver = undefined;
        this.resizeHandler = undefined;
        this.scrollHandler = undefined;
        this.stickyRafId = undefined;

        this.quantityInput = this.querySelector('.quantity__input');
      }

      scrollToTop() {
        const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

        window.scrollTo({ top: 0, left: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
      }

      connectedCallback() {
        this.initFancyboxZoomImage();
        this.initializeProductSwapUtility();
        // Skip during swap - will be initialized in postProcessCallback

        if (!ProductInfo.swapInProgress) {
          this.initStickyProductPanel();
        }

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      initFancyboxZoomImage() {
        this.zoomModalAbortController?.abort();
        this.zoomModalAbortController = new AbortController();
        const { signal } = this.zoomModalAbortController;

        const zoomModalOpener = document.querySelectorAll('[data-modal="zoom-image"]');
        const zoomModalCloser = document.querySelectorAll('[data-modal-closer]');
        const zoomModal = document.querySelector('.zoom-image-modal');
        const bodyElement = document.querySelector('body');

        if (!zoomModal) return;

        const closeZoomModal = () => {
          bodyElement.classList.remove('overflow-hidden');
          zoomModal.close();
          zoomModal.setAttribute('inert', '');
        };

        const openZoomModal = (event) => {
          event?.preventDefault();
          bodyElement.classList.add('overflow-hidden');
          zoomModal.showModal();
          zoomModal.removeAttribute('inert');
        };

        window.addEventListener('productZoomImageModal', openZoomModal, { signal });

        zoomModalOpener.forEach((item) => {
          item.addEventListener('click', openZoomModal, { signal });
        });

        zoomModalCloser.forEach((item) => {
          item.addEventListener('click', closeZoomModal, { signal });
        });

        zoomModal.addEventListener(
          'click',
          (e) => {
          const dialogDimensions = zoomModal.getBoundingClientRect();

          if (
            e.clientX < dialogDimensions.left ||
            e.clientX > dialogDimensions.right ||
            e.clientY < dialogDimensions.top ||
            e.clientY > dialogDimensions.bottom
          ) {
              closeZoomModal();
          }
          },
          { signal }
        );

        document.addEventListener(
          'keydown',
          (e) => {
            if (e.key === 'Escape' && zoomModal.hasAttribute('open')) {
              closeZoomModal();
            }
          },
          { signal }
        );

        const gallerySlider = document.querySelector('.product-gallery');

        if (gallerySlider) {
          gallerySlider.addEventListener(
            'click',
            (e) => {
              const target = e.target.closest('.product-gallery-slider__img');

              if (!target) return;

              const targetPosition = target.getAttribute('data-position');
              const modalImage = zoomModal.querySelector(`.zoom-image-modal__img[data-position="${targetPosition}"]`);

              if (modalImage) {
                modalImage.scrollIntoView({ behavior: 'smooth' });
              }
            },
            { signal }
          );
        }
      }

      initStickyProductPanel() {
        // Clean up previous observers/handlers to avoid duplicates
        if (this.productFormObserver) {
          this.productFormObserver.disconnect();
          this.productFormObserver = undefined;
        }

        if (this.resizeHandler) {
          window.removeEventListener('resize', this.resizeHandler);
          this.resizeHandler = undefined;
        }

        if (this.scrollHandler) {
          window.removeEventListener('scroll', this.scrollHandler);
          this.scrollHandler = undefined;
        }

        if (this.stickyRafId) {
          cancelAnimationFrame(this.stickyRafId);
          this.stickyRafId = undefined;
        }

        // There are 2 product forms on PDP (main + sticky panel).
        // We must base visibility on the MAIN add-to-cart submit button.
        const stickyPanel = this.querySelector('.sticky-bottom-panel');
        const mainProductFormWrapper = this.getMainProductFormElement();

        if (!mainProductFormWrapper || !stickyPanel) return;

        const getMainAddToCartButton = () =>
          this.getMainProductFormElement()?.querySelector('button[type="submit"][name="add"]');

        const computeIsMainFormVisible = () => {
          const addToCartButton = getMainAddToCartButton();

          if (addToCartButton) return this.isElementVisibleInViewport(addToCartButton);

          return this.isElementVisibleInViewport(this.getMainProductFormElement());
        };

        // Observe a stable wrapper element, but always compute visibility from the main submit button.
        const visibilityTarget = mainProductFormWrapper;

        const updatePanelVisibility = (isFormVisible) => {
          // During product swap the observed form can temporarily become non-intersecting.
          // Keep sticky panel hidden to avoid a flash.
          if (ProductInfo.swapInProgress) {
            stickyPanel.classList.remove('visible');
            stickyPanel.setAttribute('inert', '');

            return;
          }

          if (isFormVisible) {
            stickyPanel.classList.remove('visible');
            stickyPanel.setAttribute('inert', '');
          } else {
            stickyPanel.classList.add('visible');
            stickyPanel.removeAttribute('inert');
          }
        };

        const checkInitialState = () => {
          updatePanelVisibility(computeIsMainFormVisible());
        };

        const scheduleCheck = () => {
          if (this.stickyRafId) cancelAnimationFrame(this.stickyRafId);

          this.stickyRafId = requestAnimationFrame(() => {
            this.stickyRafId = undefined;
            checkInitialState();
          });
        };

        // Check state synchronously first to prevent flash
        checkInitialState();
        
        const handleResize = () => {
          scheduleCheck();
        };
        
        window.addEventListener('resize', handleResize);

        const handleScroll = () => {
          scheduleCheck();
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        
        const observer = new IntersectionObserver(() => {
          scheduleCheck();
        }, { threshold: 0 });

        observer.observe(visibilityTarget);
        
        this.productFormObserver = observer;
        this.resizeHandler = handleResize;
        this.scrollHandler = handleScroll;
      }

      getMainProductFormElement() {
        const productForms = Array.from(this.querySelectorAll('.product-form'));

        return productForms.find((formElement) => !formElement.closest('.sticky-bottom-panel')) || productForms[0];
      }

      isElementVisibleInViewport(element) {
        if (!element) return false;

        const rect = element.getBoundingClientRect();

        return rect.bottom > 0 && rect.top < window.innerHeight;
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');

        if (!this.quantityForm) return;

        this.setQuantityBoundries();

        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();

        this.zoomModalAbortController?.abort();
        
        if (this.productFormObserver) {
          this.productFormObserver.disconnect();
        }

        if (this.resizeHandler) {
          window.removeEventListener('resize', this.resizeHandler);
        }

        if (this.scrollHandler) {
          window.removeEventListener('scroll', this.scrollHandler);
        }

        if (this.stickyRafId) {
          cancelAnimationFrame(this.stickyRafId);
        }
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );

        // Ensure sticky panel in new HTML starts hidden
        this.preProcessHtmlCallbacks.push((html) => {
          const newStickyPanel = html.querySelector('.sticky-bottom-panel');

          if (newStickyPanel) {
            newStickyPanel.classList.remove('visible');
            newStickyPanel.setAttribute('inert', '');
          }
        });

        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
          // Re-init sticky panel after DOM is stable.
          const productInfo = newNode.matches?.('product-info') ? newNode : newNode.querySelector('product-info');

          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              // Keep sticky hidden during init, then force a first recompute after swap ends.
              productInfo?.initStickyProductPanel?.();
              ProductInfo.swapInProgress = false;
              requestAnimationFrame(() => {
                if (ProductInfo.scrollToTopAfterSwap) {
                  ProductInfo.scrollToTopAfterSwap = false;
                  productInfo?.scrollToTop?.();
                }

                window.dispatchEvent(new Event('scroll'));
              });
            });
          });
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;

        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        const isSwatchPickerChange =
          !!target?.closest?.('.product-form__input--swatch') ||
          (target?.tagName === 'SELECT' && !!target.selectedOptions?.[0]?.dataset?.optionSwatchValue);

        if (isSwatchPickerChange && !shouldFetchFullPage) {
          this.scrollToTop();
        }

        if (shouldFetchFullPage) {
          ProductInfo.scrollToTopAfterSwap = true;
          this.scrollToTop();
        }

        // Prevent sticky panel flash during product swap: hide it immediately on option click.
        // During swap, IntersectionObserver can briefly report a false "not visible" state.
        if (shouldSwapProduct) {
          ProductInfo.swapInProgress = true;
          const currentStickyPanel = this.querySelector('.sticky-bottom-panel');

          if (currentStickyPanel) {
            currentStickyPanel.classList.remove('visible');
            currentStickyPanel.setAttribute('inert', '');
          }
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        this.productForms.forEach((productForm) => {
          productForm?.toggleSubmitButton?.(true);
          productForm?.handleErrorMessage?.();
        });
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();
          ProductInfo.swapInProgress = true;

          // Hide current sticky panel immediately before swap
          const currentStickyPanel = this.querySelector('.sticky-bottom-panel');

          if (currentStickyPanel) {
            currentStickyPanel.classList.remove('visible');
            currentStickyPanel.setAttribute('inert', '');
          }

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal, credentials: 'include' })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');

            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;

        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const updatedVariantSelects = html.querySelector('variant-selects');
        const currentVariantSelects = this.variantSelectors;

        if (!updatedVariantSelects || !currentVariantSelects) return;

        // Keep the existing DOM to avoid re-rendering swatch thumbnails.
        // Only sync availability/disabled state + selected variant JSON.
        const updatedSelectedVariant = updatedVariantSelects.querySelector('[data-selected-variant]');
        const currentSelectedVariant = currentVariantSelects.querySelector('[data-selected-variant]');

        if (updatedSelectedVariant && currentSelectedVariant) {
          currentSelectedVariant.innerHTML = updatedSelectedVariant.innerHTML;
        }

        updatedVariantSelects.querySelectorAll('[data-option-value-id]').forEach((updatedInput) => {
          const optionValueId = updatedInput.dataset.optionValueId;

          if (!optionValueId) return;

          const currentInput = currentVariantSelects.querySelector(`[data-option-value-id="${optionValueId}"]`);

          if (!currentInput) return;

          const shouldDisable = updatedInput.hasAttribute('disabled');

          currentInput.toggleAttribute('disabled', shouldDisable);

          const shouldBeVisuallyDisabled = updatedInput.classList.contains('visually-disabled');

          currentInput.classList.toggle('visually-disabled', shouldBeVisuallyDisabled);
        });
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          const sourceGallery = html.getElementById(`ProductGallery-${this.sectionId}`);
          const destinationGallery = this.querySelector(`#ProductGallery-${this.dataset.section}`);

          if (sourceGallery && destinationGallery) {
            destinationGallery.innerHTML = sourceGallery.innerHTML;
            this.initFancyboxZoomImage();
          }

          if (!variant) {
            this.setUnavailable();

            return;
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);

            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const sourceProductForms = Array.from(html.querySelectorAll('.product-form'));

          const sourceMainProductForm =
            sourceProductForms.find((formElement) => !formElement.closest('.sticky-bottom-panel')) || sourceProductForms[0];

          const sourceMainSubmitButton = sourceMainProductForm?.querySelector('button[type="submit"][name="add"]');
          const shouldDisableSubmit = sourceMainSubmitButton?.hasAttribute('disabled') ?? true;

          this.productForms.forEach((productForm) => {
            productForm?.toggleSubmitButton?.(shouldDisableSubmit, window.variantStrings.soldOut);
          });

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-sticky-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');

          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;

        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForms.forEach((productForm) => {
          productForm?.toggleSubmitButton?.(true, window.variantStrings.unavailable);
        });

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');

        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;

        if (max !== null) min = Math.min(min, max);

        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }

        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;

        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`, {
          credentials: 'include',
        })
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');

            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];

        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);

          if (!current || !updated) continue;

          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];

            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);

              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productForms() {
        return Array.from(this.querySelectorAll('product-form'));
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );

        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );

        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
  }

  customElements.define('product-info', ProductInfo);
}
